import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";

import Layout from "@/components/layout/Layout";
import { CatalogProvider } from "@/lib/catalog-context";
import { BuyerAuthProvider } from "@/lib/buyer-auth";
import { AdminAuthProvider } from "@/lib/admin-auth";

import Home from "@/pages/Home";
import ServicesList from "@/pages/ServicesList";
import ServiceDetail from "@/pages/ServiceDetail";
import PackageDetail from "@/pages/PackageDetail";
import PackagesList from "@/pages/PackagesList";
import FAQs from "@/pages/FAQs";
import Help from "@/pages/Help";
import Policy from "@/pages/Policy";
import SearchPage from "@/pages/Search";
import Plan from "@/pages/Plan";
import Track from "@/pages/Track";
import QuoteSuccess from "@/pages/QuoteSuccess";
import QuoteDetail from "@/pages/QuoteDetail";
import NotFound from "@/pages/NotFound";

// Admin (eager: layout + auth gating; lazy: heavier inner pages)
import AdminLayout from "@/components/admin/AdminLayout";
import AdminProtectedRoute from "@/components/admin/AdminProtectedRoute";
import AdminLogin from "@/pages/admin/AdminLogin";
import { Toaster } from "@/components/ui/sonner";

const AdminQuotes = lazy(() => import("@/pages/admin/AdminQuotes"));
const AdminQuoteDetail = lazy(() => import("@/pages/admin/AdminQuoteDetail"));
const AdminTalent = lazy(() => import("@/pages/admin/AdminTalent"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminCmsHub = lazy(() => import("@/pages/admin/AdminCmsHub"));
const AdminCmsList = lazy(() => import("@/pages/admin/AdminCmsList"));
const AdminCmsPricingRules = lazy(() =>
  import("@/pages/admin/AdminCmsPricingRules"),
);
const AdminCmsPricingCalendar = lazy(() =>
  import("@/pages/admin/AdminCmsPricingCalendar"),
);
const AdminCmsQuestionSets = lazy(() =>
  import("@/pages/admin/AdminCmsQuestionSets"),
);
const AdminCmsTaxSettings = lazy(() =>
  import("@/pages/admin/AdminCmsTaxSettings"),
);
const AdminReports = lazy(() => import("@/pages/admin/AdminReports"));
const AdminAuditLogs = lazy(() => import("@/pages/admin/AdminAuditLogs"));

function RouteFade({ children }) {
  const { pathname } = useLocation();
  return (
    <div key={pathname} className="animate-fade-in">
      {children}
    </div>
  );
}

function BuyerRoutes() {
  return (
    <Layout>
      <RouteFade>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<ServicesList />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/packages" element={<PackagesList />} />
          <Route path="/packages/:id" element={<PackageDetail />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/help" element={<Help />} />
          <Route path="/policies/:slug" element={<Policy />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/plan" element={<Plan />} />
          <Route path="/track" element={<Track />} />
          <Route path="/login" element={<Track />} />
          <Route path="/quotes/:id/success" element={<QuoteSuccess />} />
          <Route path="/quotes/:id" element={<QuoteDetail />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </RouteFade>
    </Layout>
  );
}

function AdminPageFallback() {
  return (
    <div
      data-testid="admin-page-fallback"
      className="flex min-h-[40vh] items-center justify-center text-sm text-[#0F172A]/55"
    >
      Loading…
    </div>
  );
}

function CmsListByParam() {
  // URL: /admin/cms/:entity  — useParams resolved inside AdminCmsList.
  return <AdminCmsList />;
}

function AdminRoutes() {
  return (
    <Routes>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<AdminPageFallback />}>
              <AdminQuotes />
            </Suspense>
          }
        />
        <Route
          path="quotes"
          element={
            <Suspense fallback={<AdminPageFallback />}>
              <AdminQuotes />
            </Suspense>
          }
        />
        <Route
          path="quotes/:id"
          element={
            <Suspense fallback={<AdminPageFallback />}>
              <AdminQuoteDetail />
            </Suspense>
          }
        />
        <Route
          path="talent"
          element={
            <Suspense fallback={<AdminPageFallback />}>
              <AdminTalent />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <AdminProtectedRoute requiredRoles={["super_admin"]}>
              <Suspense fallback={<AdminPageFallback />}>
                <AdminUsers />
              </Suspense>
            </AdminProtectedRoute>
          }
        />

        {/* CMS hub + sub-editors */}
        <Route
          path="cms"
          element={
            <AdminProtectedRoute requiredRoles={["super_admin", "content"]}>
              <Suspense fallback={<AdminPageFallback />}>
                <AdminCmsHub />
              </Suspense>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="cms/pricing-rules"
          element={
            <AdminProtectedRoute requiredRoles={["super_admin", "content"]}>
              <Suspense fallback={<AdminPageFallback />}>
                <AdminCmsPricingRules />
              </Suspense>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="cms/pricing-calendar"
          element={
            <AdminProtectedRoute requiredRoles={["super_admin", "content"]}>
              <Suspense fallback={<AdminPageFallback />}>
                <AdminCmsPricingCalendar />
              </Suspense>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="cms/occasion-questions"
          element={
            <AdminProtectedRoute requiredRoles={["super_admin", "content"]}>
              <Suspense fallback={<AdminPageFallback />}>
                <AdminCmsQuestionSets />
              </Suspense>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="cms/tax-settings"
          element={
            <AdminProtectedRoute requiredRoles={["super_admin", "content"]}>
              <Suspense fallback={<AdminPageFallback />}>
                <AdminCmsTaxSettings />
              </Suspense>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="cms/:entity"
          element={
            <AdminProtectedRoute requiredRoles={["super_admin", "content"]}>
              <Suspense fallback={<AdminPageFallback />}>
                <CmsListByParam />
              </Suspense>
            </AdminProtectedRoute>
          }
        />

        {/* Reports */}
        <Route
          path="reports"
          element={
            <AdminProtectedRoute
              requiredRoles={["super_admin", "finance", "operations"]}
            >
              <Suspense fallback={<AdminPageFallback />}>
                <AdminReports />
              </Suspense>
            </AdminProtectedRoute>
          }
        />

        {/* Audit logs */}
        <Route
          path="audit-logs"
          element={
            <AdminProtectedRoute requiredRoles={["super_admin"]}>
              <Suspense fallback={<AdminPageFallback />}>
                <AdminAuditLogs />
              </Suspense>
            </AdminProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

function AppRoutes() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return isAdmin ? <AdminRoutes /> : <BuyerRoutes />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <CatalogProvider>
          <BuyerAuthProvider>
            <AdminAuthProvider>
              <AppRoutes />
              <Toaster />
            </AdminAuthProvider>
          </BuyerAuthProvider>
        </CatalogProvider>
      </BrowserRouter>
    </div>
  );
}
